import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Link from '@mui/material/Link';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import DistanceZipOptions from 'common/components/DistanceZipOptions';
import PriceRange from 'common/components/PriceRange';
import YearRange from 'common/components/YearRange';
import { useMakes } from 'common/hooks/useMakes';
import { useModels } from 'common/hooks/useModels';
import {
  selectBodyTypeOptions,
  selectColorTypeOptions,
  selectConditionTypeOptions,
  selectCylinderTypeOptions,
  selectDoorTypeOptions,
  selectDrivetrainTypeOptions,
  selectFuelTypeOptions,
  selectMileageRangeOptions,
  selectTitleStatusOptions,
  selectTransmissionTypeOptions,
} from 'common/hooks/useSelectOptions';
import { SelectBuildOption } from 'common/tools/select-option-tools';
import { PageStyles } from 'common/types/PageStyles';
import { StrArrayParams } from 'common/types/SearchParams';
import _ from 'lodash';
import React, { PropsWithChildren } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { cacheActions, cacheSelectors } from 'redux/cache.slice';
import { RootState } from 'redux/root-reducer';
import { searchParamActions, searchParamSelectors } from 'redux/search-param.slice';
import OptionAccordion from 'stories/inputs/OptionAccordion';
import { WsNativeSelect } from 'stories/inputs/WsNativeSelect';

interface Props {
  sx?: any;
}

const SearchBar = (props: PropsWithChildren<Props>) => {
  const dispatch = useDispatch();

  const { makeOptions, selectedMakes, addMakeKey, removeMakeKey } = useMakes();
  const { getModelOptions, getSelectedModels, addModelKey, removeModelKey } = useModels();

  const searchAccordianState = useSelector((state: RootState) => cacheSelectors.searchAccordianState(state));
  const makesPopularFilter = useSelector((state: RootState) => cacheSelectors.makesPopularFilter(state));
  const bodys = useSelector((state: RootState) => searchParamSelectors.bodys(state));
  const colors = useSelector((state: RootState) => searchParamSelectors.colors(state));
  const conditions = useSelector((state: RootState) => searchParamSelectors.conditions(state));
  const cylinders = useSelector((state: RootState) => searchParamSelectors.cylinders(state));
  const doors = useSelector((state: RootState) => searchParamSelectors.doors(state));
  const drivetrains = useSelector((state: RootState) => searchParamSelectors.drivetrains(state));
  const fuels = useSelector((state: RootState) => searchParamSelectors.fuels(state));
  const titleSs = useSelector((state: RootState) => searchParamSelectors.titleSs(state));
  const transmissions = useSelector((state: RootState) => searchParamSelectors.transmissions(state));
  const mileage = useSelector((state: RootState) => searchParamSelectors.mileage(state));

  const bodyTypeOptions = React.useMemo(() => selectBodyTypeOptions(SelectBuildOption.hideOther), []);
  const titleStatusOptions = React.useMemo(() => selectTitleStatusOptions(SelectBuildOption.hideOther), []);
  const colorTypeOptions = React.useMemo(() => selectColorTypeOptions(SelectBuildOption.hideOther), []);
  const conditionTypeOptions = React.useMemo(() => selectConditionTypeOptions(SelectBuildOption.hideOther), []);
  const cylinderTypeOptions = React.useMemo(() => selectCylinderTypeOptions(SelectBuildOption.hideOther), []);
  const doorTypeOptions = React.useMemo(() => selectDoorTypeOptions(SelectBuildOption.hideOther), []);
  const drivetrainTypeOptions = React.useMemo(() => selectDrivetrainTypeOptions(SelectBuildOption.hideOther), []);
  const fuelTypeOptions = React.useMemo(() => selectFuelTypeOptions(SelectBuildOption.hideOther), []);
  const mileageRangeOptions = React.useMemo(() => selectMileageRangeOptions(SelectBuildOption.includeStarAny), []);
  const transmissionTypeOptions = React.useMemo(() => selectTransmissionTypeOptions(SelectBuildOption.hideOther), []);

  const toggleAccordion = (key: string, expanded: boolean) => {
    const newState = _.cloneDeep(searchAccordianState);
    if (expanded && !newState.includes(key)) {
      newState.push(key);
      dispatch(cacheActions.searchAccordianState(_.orderBy(newState)));
    } else if (!expanded && newState.includes(key)) {
      newState.splice(newState.indexOf(key), 1);
      dispatch(cacheActions.searchAccordianState(_.orderBy(newState)));
    }
  };

  const handleAccordionCheckChange = (key: string, val: string, checked: boolean, groupId?: string) => {
    if (groupId) {
      if (checked) addModelKey(groupId, val);
      else removeModelKey(groupId, val);
    } else if (key === 'makes') {
      if (checked) addMakeKey(val);
      else removeMakeKey(val);
    } else {
      if (checked) dispatch(searchParamActions.addArrayKey({ key: key as keyof StrArrayParams, val }));
      else dispatch(searchParamActions.removeArrayKey({ key: key as keyof StrArrayParams, val }));
    }
  };

  return (
    <Box
      sx={{
        ...props.sx,
        padding: 1,
        height: '100%',
      }}
    >
      <Paper
        sx={{
          height: '100%',
          overflowY: 'scroll',
        }}
        elevation={2}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            padding: '16px 16px 0 16px',
          }}
        >
          <Typography color="primary" variant="h5" sx={{ marginBottom: 2 }}>
            Filter Results
          </Typography>
          <Box sx={styles.section}>
            <DistanceZipOptions />
          </Box>
          <Box sx={styles.section}>
            <Typography variant="h6" sx={{ paddingBottom: 1 }}>
              Miles:
            </Typography>
            <WsNativeSelect
              width={180}
              id="mileageRange"
              data={mileageRangeOptions}
              value={mileage}
              onChange={(event: React.ChangeEvent<HTMLSelectElement>) =>
                dispatch(searchParamActions.mileage(event.target.value))
              }
            />
          </Box>
          <Box sx={styles.section}>
            <Typography variant="h6">Year:</Typography>
            <YearRange />
          </Box>
          <Box sx={styles.section}>
            <Typography variant="h6">Price:</Typography>
            <PriceRange />
          </Box>
          <Box sx={styles.section}>
            <Typography variant="h6">Options:</Typography>
          </Box>

          <Grid container gap={1}>
            <OptionAccordion
              controlId="makes"
              headerLabel="Makes"
              data={makeOptions}
              values={selectedMakes}
              expanded={searchAccordianState.includes('makes')}
              changeValuesFn={handleAccordionCheckChange}
              toggleExpandFn={toggleAccordion}
            >
              <Typography variant="subtitle2">
                <Link
                  component="button"
                  sx={{
                    paddingRight: 6,
                  }}
                  onClick={() => dispatch(cacheActions.makesPopularFilter(!makesPopularFilter))}
                >
                  {makesPopularFilter ? 'Show All Makes' : 'Hide All Makes'}
                </Link>
              </Typography>
            </OptionAccordion>
            {selectedMakes?.length
              ? selectedMakes.map(make => (
                  <OptionAccordion
                    key={`${make}-model`}
                    controlId={`${make}-model`}
                    groupId={make}
                    headerLabel={`${make} Models`}
                    data={getModelOptions(make)}
                    values={getSelectedModels(make)}
                    expanded={searchAccordianState.includes(`${make}-model`)}
                    changeValuesFn={handleAccordionCheckChange}
                    toggleExpandFn={toggleAccordion}
                  />
                ))
              : null}
            <OptionAccordion
              controlId="bodys"
              headerLabel="Body Style"
              data={bodyTypeOptions}
              values={bodys}
              expanded={searchAccordianState.includes('bodys')}
              changeValuesFn={handleAccordionCheckChange}
              toggleExpandFn={toggleAccordion}
            />
            <OptionAccordion
              controlId="titleSs"
              headerLabel="Title Status"
              data={titleStatusOptions}
              values={titleSs}
              expanded={searchAccordianState.includes('titleSs')}
              changeValuesFn={handleAccordionCheckChange}
              toggleExpandFn={toggleAccordion}
            />
            <OptionAccordion
              controlId="colors"
              headerLabel="Color"
              data={colorTypeOptions}
              values={colors}
              expanded={searchAccordianState.includes('colors')}
              changeValuesFn={handleAccordionCheckChange}
              toggleExpandFn={toggleAccordion}
            />
            <OptionAccordion
              controlId="doors"
              headerLabel="Doors"
              data={doorTypeOptions}
              values={doors}
              expanded={searchAccordianState.includes('doors')}
              changeValuesFn={handleAccordionCheckChange}
              toggleExpandFn={toggleAccordion}
            />
            <OptionAccordion
              controlId="conditions"
              headerLabel="Condition"
              data={conditionTypeOptions}
              values={conditions}
              expanded={searchAccordianState.includes('conditions')}
              changeValuesFn={handleAccordionCheckChange}
              toggleExpandFn={toggleAccordion}
            />
            <OptionAccordion
              controlId="drivetrains"
              headerLabel="Drivetrain"
              data={drivetrainTypeOptions}
              values={drivetrains}
              expanded={searchAccordianState.includes('drivetrains')}
              changeValuesFn={handleAccordionCheckChange}
              toggleExpandFn={toggleAccordion}
            />
            <OptionAccordion
              controlId="cylinders"
              headerLabel="Engine Cylinders"
              data={cylinderTypeOptions}
              values={cylinders}
              expanded={searchAccordianState.includes('cylinders')}
              changeValuesFn={handleAccordionCheckChange}
              toggleExpandFn={toggleAccordion}
            />
            <OptionAccordion
              controlId="fuels"
              headerLabel="Fuel"
              data={fuelTypeOptions}
              values={fuels}
              expanded={searchAccordianState.includes('fuels')}
              changeValuesFn={handleAccordionCheckChange}
              toggleExpandFn={toggleAccordion}
            />
            <OptionAccordion
              controlId="transmissions"
              headerLabel="Transmission"
              data={transmissionTypeOptions}
              values={transmissions || []}
              expanded={searchAccordianState.includes('transmissions')}
              changeValuesFn={handleAccordionCheckChange}
              toggleExpandFn={toggleAccordion}
            />
          </Grid>
        </Box>
      </Paper>
    </Box>
  );
};

const styles: PageStyles = {
  section: {
    display: 'flex',
    marginTop: 0,
    flexDirection: 'column',
    // border: '1px solid blue',
  },
};

export default SearchBar;
